import { type MouseEvent, type ReactNode } from 'react';

import { busiest, HEAT_STEPS, heatStep } from '@/features/reports/heatmap/heatStep';
import { cn } from '@/util/utils';

/** Engagement's sage ramp or demand's stone one: the two grids never read as the same measure. */
export type HeatTone = 'heat' | 'demand';

// Spelled out so Tailwind sees every class. Ink on the pale steps, paper on the dark ones:
// contrast decides it, not taste (screens-reports.css).
const STEPS: Record<HeatTone, readonly string[]> = {
    heat: [
        '',
        'bg-heat-1 text-foreground',
        'bg-heat-2 text-foreground',
        'bg-heat-3 text-primary-foreground',
        'bg-heat-4 text-primary-foreground',
        'bg-heat-5 text-primary-foreground',
    ],
    demand: [
        '',
        'bg-demand-1 text-foreground',
        'bg-demand-2 text-foreground',
        'bg-demand-3 text-primary-foreground',
        'bg-demand-4 text-primary-foreground',
        'bg-demand-5 text-primary-foreground',
    ],
};

const SWATCHES: Record<HeatTone, readonly string[]> = {
    heat: ['bg-heat-1', 'bg-heat-2', 'bg-heat-3', 'bg-heat-4', 'bg-heat-5'],
    demand: ['bg-demand-1', 'bg-demand-2', 'bg-demand-3', 'bg-demand-4', 'bg-demand-5'],
};

const HEAD =
    'text-muted-foreground font-condensed px-1 pb-2 text-[10px] font-medium tracking-[0.12em] whitespace-nowrap uppercase';

export interface HeatGridRow {
    key: string;
    /** The row's name, for the cells' titles. */
    name: string;
    /** What the row header draws: the name, or a link that opens the row. */
    header: ReactNode;
    /** One per column; null where there is nothing, drawn as nothing rather than a zero. */
    scores: (number | null)[];
    trailing?: ReactNode;
}

/**
 * A heatmap as a table (ALS-88): a row header, a near-square cell per column shaded on a five-step
 * ramp against the busiest cell on screen, and an optional column after the cells. The grid takes
 * the width its cells need and scrolls sideways inside a labelled region on a phone, its row
 * headers held in place, so a week or a category is never off the edge of the page.
 *
 * An openable grid opens a row from anywhere on it, through the link its header draws, so a
 * keyboard and a screen reader reach the same place by that link.
 */
export function HeatGrid({
    label,
    rowHead,
    columns,
    rows,
    tone,
    cellLabel,
    emptyLabel,
    trailingHead,
    openable = false,
}: {
    label: string;
    rowHead: string;
    columns: string[];
    rows: HeatGridRow[];
    tone: HeatTone;
    cellLabel: (row: string, column: string, value: number) => string;
    /** Read out for an empty cell. */
    emptyLabel: string;
    trailingHead?: string;
    openable?: boolean;
}) {
    const max = busiest(rows.map((r) => r.scores));

    const open = (event: MouseEvent<HTMLTableRowElement>) => {
        if (!openable || (event.target as HTMLElement).closest('a')) return;
        event.currentTarget.querySelector('a')?.click();
    };

    // `relative` makes the box the empty cells' sr-only labels sit in (ALS-480). Without it they
    // are placed against the page, outside this scroll, and widen a phone's page into a blank strip.
    return (
        <div
            role="region"
            aria-label={label}
            tabIndex={0}
            className="focus-visible:ring-ring relative -mx-1 overflow-x-auto overscroll-x-contain px-1 focus-visible:ring-2 focus-visible:outline-none"
        >
            <table aria-label={label} className="w-auto border-separate border-spacing-[3px]">
                <thead>
                    <tr>
                        <th
                            scope="col"
                            className={cn(HEAD, 'bg-card sticky left-0 pr-3 text-left')}
                        >
                            {rowHead}
                        </th>
                        {columns.map((column, i) => (
                            // By position: two categories can share a name.
                            <th key={i} scope="col" className={cn(HEAD, 'text-center')}>
                                {column}
                            </th>
                        ))}
                        {trailingHead ? (
                            <th scope="col" className={cn(HEAD, 'pl-4 text-left')}>
                                {trailingHead}
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={row.key}
                            onClick={openable ? open : undefined}
                            className={cn(openable && 'group cursor-pointer')}
                        >
                            <th
                                scope="row"
                                className="bg-card sticky left-0 pr-3 text-left text-sm font-medium whitespace-nowrap group-hover:underline"
                            >
                                {row.header}
                            </th>
                            {row.scores.map((value, i) => {
                                const column = columns[i] ?? '';
                                const step = heatStep(value, max);
                                return (
                                    <td
                                        key={i}
                                        title={
                                            value === null
                                                ? undefined
                                                : cellLabel(row.name, column, value)
                                        }
                                        className={cn(
                                            'h-9 min-w-[52px] rounded-sm text-center text-xs tabular-nums',
                                            step === 0 ? 'bg-muted/60' : STEPS[tone][step],
                                        )}
                                    >
                                        {value === null ? (
                                            <span className="sr-only">{emptyLabel}</span>
                                        ) : (
                                            value
                                        )}
                                    </td>
                                );
                            })}
                            {trailingHead ? (
                                <td className="pl-4 text-left whitespace-nowrap">{row.trailing}</td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** The legend the ramps are useless without: five swatches between the two words that say which end is which. */
export function Legend({ tone, low, high }: { tone: HeatTone; low: string; high: string }) {
    return (
        <div className="text-muted-foreground font-condensed mt-4 flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase">
            <span className="mr-1">{low}</span>
            {SWATCHES[tone].slice(0, HEAT_STEPS).map((swatch, i) => (
                <i
                    key={swatch}
                    data-step={i + 1}
                    aria-hidden
                    className={cn('h-2.5 w-[18px] flex-none rounded-[2px]', swatch)}
                />
            ))}
            <span className="ml-1">{high}</span>
        </div>
    );
}
